import {
  AiDraftFiltersInput,
  AiDraftInitiator,
  AiDraftType,
} from 'lib-knowledge-management-common/graphql';
import { filterNonNullStrings } from 'lib-knowledge-utils/array';
import { useMemo } from 'react';
import { ArrayParam, useQueryParams } from 'use-query-params';

import { useShowInternalOnlyCollections } from 'hooks/useShowInternalOnlyCollections';

const suggestionsFiltersParams = {
  collectionId: ArrayParam,
  type: ArrayParam,
  initiatedBy: ArrayParam,
  audience: ArrayParam,
};

/** Who a draft is written for, as the backend's `internalOnly` tells them apart. */
export enum SuggestionAudience {
  Internal = 'internal',
  Customers = 'customers',
}

export type SuggestionsFilterSelection = {
  collectionIds: string[];
  types: AiDraftType[];
  initiators: AiDraftInitiator[];
  audiences: SuggestionAudience[];
};

export type UseSuggestionsFilters = {
  selection: SuggestionsFilterSelection;
  /** For `aiDrafts`; `undefined` when nothing is filtered. */
  filters: AiDraftFiltersInput | undefined;
  /** How many of the filters narrow the list. */
  activeCount: number;
  setCollectionIds: (ids: string[]) => void;
  setTypes: (types: AiDraftType[]) => void;
  setInitiators: (initiators: AiDraftInitiator[]) => void;
  setAudiences: (audiences: SuggestionAudience[]) => void;
  clear: () => void;
};

const draftTypes = Object.values(AiDraftType);
const initiators = Object.values(AiDraftInitiator);
const audiences = Object.values(SuggestionAudience);

function only<T extends string>(values: readonly T[], all: readonly T[]) {
  return values.filter((value): value is T => all.includes(value));
}

/**
 * The suggestions list filters, kept in the URL beside the search so the view
 * survives a reload and can be linked to.
 *
 * Type, initiator and audience each take one value on the backend, and have two
 * to pick from: one picked filters by it, none or both is no filter.
 *
 * The audience is dropped while internal only collections are off, so a linked
 * URL cannot filter by a filter that is not shown.
 */
export function useSuggestionsFilters(): UseSuggestionsFilters {
  const [params, setParams] = useQueryParams(suggestionsFiltersParams);
  const showInternalOnlyCollections = useShowInternalOnlyCollections();

  const selection = useMemo<SuggestionsFilterSelection>(
    () => ({
      collectionIds: filterNonNullStrings(params.collectionId) ?? [],
      types: only(
        (filterNonNullStrings(params.type) ?? []) as AiDraftType[],
        draftTypes,
      ),
      initiators: only(
        (filterNonNullStrings(params.initiatedBy) ?? []) as AiDraftInitiator[],
        initiators,
      ),
      audiences: showInternalOnlyCollections
        ? only(
            (filterNonNullStrings(params.audience) ??
              []) as SuggestionAudience[],
            audiences,
          )
        : [],
    }),
    [
      params.collectionId,
      params.type,
      params.initiatedBy,
      params.audience,
      showInternalOnlyCollections,
    ],
  );

  const filters = useMemo<AiDraftFiltersInput | undefined>(() => {
    const input: AiDraftFiltersInput = {
      ...(selection.collectionIds.length && {
        collectionIds: selection.collectionIds,
      }),
      ...(selection.types.length === 1 && { type: selection.types[0] }),
      ...(selection.initiators.length === 1 && {
        initiatedBy: selection.initiators[0],
      }),
      ...(selection.audiences.length === 1 && {
        internalOnly: selection.audiences[0] === SuggestionAudience.Internal,
      }),
    };
    return Object.keys(input).length ? input : undefined;
  }, [selection]);

  // `undefined` drops a param rather than leaving `?type=` behind.
  const set = (values: Partial<Record<keyof typeof params, string[]>>) =>
    setParams(
      Object.fromEntries(
        Object.entries(values).map(([key, list]) => [
          key,
          list?.length ? list : undefined,
        ]),
      ),
      'replaceIn',
    );

  return {
    selection,
    filters,
    activeCount: filters ? Object.keys(filters).length : 0,
    setCollectionIds: (ids) => set({ collectionId: ids }),
    setTypes: (types) => set({ type: types }),
    setInitiators: (values) => set({ initiatedBy: values }),
    setAudiences: (values) => set({ audience: values }),
    clear: () =>
      set({ collectionId: [], type: [], initiatedBy: [], audience: [] }),
  };
}
